import { EventBus } from '../event-bus.js';

export const contextMenuMixin = {
  data() {
    return {
      contextMenuItems: [],
      isCKEditor: false
    }
  },

  computed: {
    isReadOnlyState() {
      throw new Error('isReadOnlyState must be implemented by component');
    },
    
    inputValue() {
      throw new Error('inputValue must be implemented by component');
    },
    
    inputElementId() {
      throw new Error('inputElementId must be implemented by component');
    }
  },

  methods: {
     // --- 🔧 Helpers base ----------------------------------------
    getActiveEditor() {
      const ref = this.getCKEditorRef ? this.getCKEditorRef() : this.ckeditorInstanceRef;
      const editor =
        ref?.editorInstance ||
        ref?.instance ||
        ref?.$data?.instance ||
        ref?.$options?.editor ||
        null;
      return editor;
    },

    isCKEditor4(editor) {
      return !!editor?.insertHtml;
    },

    isCKEditor5(editor) {
      return !!editor?.model;
    },

    hasSelection() {
      try {
        const editor = this.getActiveEditor();
        if (this.isCKEditor5(editor)) {
          const selection = editor.model.document.selection;
          return !selection.isCollapsed;
        }
        if (this.isCKEditor4(editor)) {
          const selectedText = editor.getSelection()?.getSelectedText?.();
          return !!selectedText?.length;
        }

        const el = document.getElementById(this.inputElementId);
        if (el && typeof el.selectionStart === 'number')
          return el.selectionStart !== el.selectionEnd;

        const sel = window.getSelection();
        return !!sel && !sel.isCollapsed;
      } catch (err) {
        console.error('[Mixin] hasSelection error:', err);
        return false;
      }
    },

    /**
     * 🧠 Devuelve el texto seleccionado (si existe)
     */
    getSelectedText() {
      try {
        const editor = this.getActiveEditor();
        if (this.isCKEditor5(editor)) {
          return editor.model.document.selection.getSelectedText?.() || '';
        }
        if (this.isCKEditor4(editor)) {
          return editor.getSelection()?.getSelectedText?.() || '';
        }

        const el = document.getElementById(this.inputElementId);
        if (el && typeof el.selectionStart === 'number')
          return el.value.substring(el.selectionStart, el.selectionEnd);

        const sel = window.getSelection();
        return sel?.toString() || '';
      } catch {
        return '';
      }
    },

    // Main method for loading menu items
    loadContextMenuItems(additionalItems = [], enableSystemOptions = true) {
      const currentValue = this.inputValue || '';
      const hasText = currentValue && currentValue.length > 0;
      const isReadOnly = this.isReadOnlyState;
      const hasSelection = this.hasSelection();

      const menuItems = [];

      // ADDITIONAL ITEMS
      if (additionalItems.length > 0) {
        additionalItems.forEach(item => {
          menuItems.push({
            text: item.text,
            id: item.id,
            disabled: false
          });
        });
      }
      // ADDITIONAL separator if additionalItems exist
      if (additionalItems.length > 0) {
        menuItems.push({ separator: true });
      }

      // SYSTEM OPTIONS
      if (enableSystemOptions) {
        menuItems.push({ 
          text: this.$t('ContextMenu.Copy'), 
          id: "copy",
          disabled: !hasSelection
        });

        menuItems.push({ 
          text: this.$t('ContextMenu.Cut'), 
          id: "cut",
          disabled: !hasSelection || isReadOnly
        });

        menuItems.push({ 
          text: this.$t('ContextMenu.Paste'), 
          id: "paste",
          disabled: isReadOnly
        });

        // NEW OPTION: DELETE - Selected text only
        menuItems.push({ 
          text: this.$t('Shared.TitleDelete'), 
          id: "delete",
          disabled: !hasSelection || isReadOnly 
        });

        menuItems.push({ separator: true });
      }

      // VOICE FEATURES
      if (this.shouldShowSpeechFeatures()) {
        
        const isRecordingSupported = !!this.$root.$refs.MdTextSpeech?.isSpeechRecognitionSupported;
        const isAIDictationSupported = !!this.$root.$refs.MdAIDictationSpeech?.isUsingPremiumDictation;

        if(!isAIDictationSupported) {
          menuItems.push({ 
            text: this.$t('MdTextSpeech.record'), 
            id: "recording",
            disabled: !isRecordingSupported || isReadOnly
          });
        }
        else {
          menuItems.push({ 
            text: this.$t('MdTextSpeech.aiRecord'), 
            id: "aiRecording",
            disabled: !isAIDictationSupported || isReadOnly
          });
        }

        menuItems.push({ 
          text: this.$t('MdTextSpeech.speech'), 
          id: "speech",
          disabled: !hasText
        });
      }

      this.contextMenuItems = menuItems;
      return menuItems;
    },

    // Rendering items with visual disabled
    onItemRender(args) {
      const configItem = this.contextMenuItems.find(item => 
        item.id === args.item.id && !item.separator
      );
      
      if (configItem && configItem.disabled) {
        // Apply styles directly to the DOM element
        args.element.style.opacity = '0.5';
        args.element.style.pointerEvents = 'none';
        args.element.style.cursor = 'not-allowed';
        args.element.style.color = '#6c757d';
        
        // Add accessibility attributes
        args.element.setAttribute('aria-disabled', 'true');
        args.element.setAttribute('disabled', 'true');
        args.element.classList.add('context-menu-disabled');
        
        // Prevent click events
        args.element.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };
      }
    },

    // Menu selection handler
    onContextMenuSelect(args) {
      const id = args.item.id;
      if (!id) return;

      const isReadOnly = this.isReadOnlyState;
      const hasSelection = this.hasSelection();
      // Check if the item is disabled in the DOM
      const isDisabledInDOM = args.element && (
        args.element.classList.contains('context-menu-disabled') ||
        args.element.style.pointerEvents === 'none'
      );
      
      if (isDisabledInDOM) {
        return;
      }

      switch(args.item.id) {
        case 'copy':
          if (hasSelection) {
            this.handleCopySelection();
          }
          break;
        case 'cut':
          if (!isReadOnly && hasSelection) {
            this.handleCutSelection();
          }
          break;
        case 'delete':
          if (!isReadOnly && hasSelection) {
            this.handleDeleteSelection();
          }
          break;
        case 'paste':
          if (!isReadOnly) this.handlePaste();
          break;
        case 'recording':
          if (!isReadOnly && this.hasSpeechFeatures()) this.handleRecording();
          break;
        case 'aiRecording':
          if (!isReadOnly && this.hasAIDictationFeatures()) {
            this.handleAIDictation();
          }
          break;
        case 'speech':
          if (this.inputValue && this.inputValue.length > 0 && this.hasSpeechFeatures()) {
            this.handleSpeech();
          }
          break;
        default:
          this.$emit('context-menu-select', args.item.id);
          break;
      }
    },

    // Speech features control methods
    shouldShowSpeechFeatures() {
      return this.hasSpeechFeatures() && this.isSpeechEnabled() && this.hasAIDictationFeatures();
    },

    hasSpeechFeatures() {
      return 'MdTextSpeech' in this.$options.components && this.$root.$refs.MdTextSpeech;
    },

    hasAIDictationFeatures() {
      return 'MdAIDictationSpeech' in this.$options.components && this.$root.$refs.MdAIDictationSpeech;
    },

    isSpeechEnabled() {
      return true; // Override in component if needed
    },

    // Methods for clipboard operations with fallbacks
    fallbackCopyText(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    },

    showCopyFallbackAlert(text) {
      // If all fails, show text for manual copy
      alert(`Please copy the following text manually:\n\n${text}`);
      console.log('📝 Text for manual copy:', text);
    },
  

    // Modify existing handlers to only work with selection
    async handleCopySelection() {
      const selectedText = this.getSelectedText();
      if (!selectedText) return;
      try {
        await navigator.clipboard.writeText(selectedText);
      } catch {
        this.fallbackCopyText(selectedText);
      }
    },

    async handleCutSelection() {
      try {
        const text = this.getSelectedText();
        if (!text) return;
        await navigator.clipboard.writeText(text);
        this.deleteSelection();
        this.setChangedStatus();
      } catch (err) {
        console.error('Cut failed:', err);
      }
    },

    async handleDeleteSelection() {
       try {
        if (!this.hasSelection()) return;
        this.deleteSelection();
        this.setChangedStatus?.();
      } catch (err) {
        console.error('Delete failed:', err);
      }
    },

    async handlePaste() {
      try {
        const text = await navigator.clipboard.readText();
        if (!text) return;
        this.insertText(text);
        this.setChangedStatus();
      } catch (err) {
        console.error('Paste failed:', err);
      }
    },

    insertText(text) {
      try {
        const editor = this.getActiveEditor();
        if (this.isCKEditor5(editor)) {
          editor.model.change(writer => {
            writer.insertText(text, editor.model.document.selection.getFirstPosition());
          });
          return;
        }
        if (this.isCKEditor4(editor)) {
          editor.focus();
          editor.insertHtml(text);
          return;
        }

        const el = document.getElementById(this.inputElementId);
        if (el) {
          const start = el.selectionStart ?? el.value.length;
          const end = el.selectionEnd ?? el.value.length;
          const newValue = el.value.substring(0, start) + text + el.value.substring(end);
          this.updateValue(newValue);
        }
      } catch (err) {
        console.error('[Mixin] insertText error:', err);
      }
    },

    deleteSelection() {
      try {
        const editor = this.getActiveEditor();
        if (this.isCKEditor5(editor)) {
          editor.model.change(writer => {
            writer.deleteContent(editor.model.document.selection);
          });
          return;
        }
        if (this.isCKEditor4(editor)) {
          const sel = editor.getSelection();
          if (sel && !sel.isCollapsed()) {
            editor.insertHtml(''); // reemplaza la selección
          }
          return;
        }

        const el = document.getElementById(this.inputElementId);
        if (el && typeof el.selectionStart === 'number') {
          const start = el.selectionStart;
          const end = el.selectionEnd;
          const newValue = el.value.substring(0, start) + el.value.substring(end);
          this.updateValue(newValue);
        }
      } catch (err) {
        console.error('[Mixin] deleteSelection error:', err);
      }
    },


    handleRecording() {
      EventBus.$emit('speechBarLabel', this.$t('MdTextSpeech.stoprecording'));
      this.$root.$refs.MdTextSpeech.toggleSpeechBar();
      let controlId = null;

      try {
        // We try to get the controlId directly from the component that has the menu
        if (typeof this.getControlId === 'function') {
          controlId = this.getControlId();
        }
        // If it doesn't exist in this component, we look for it in the child (e.g. MdMemo)
        else if (this.$children && this.$children.length > 0) {
          const child = this.$children.find(
            (c) => typeof c.getControlId === 'function'
          );
          if (child) {
            controlId = child.getControlId();
          }
        }
        // If we still don't find it, we try to deduce it by the target
        if (!controlId && this.targetId) {
          controlId = this.targetId.replace(/^target-/, '');
        }

        if (controlId && this.$root.$refs.MdTextSpeech) {
          this.$root.$refs.MdTextSpeech.startSpeechToText(controlId);
        } else {
          console.warn('[ContextMenuMixin] No se pudo determinar el ControlId o SpeechBar no disponible.');
        }
      } catch (err) {
        console.error('[ContextMenuMixin] Error al iniciar Speech-to-Text:', err);
      }
    },

    handleAIDictation() {
      EventBus.$emit('speechBarLabel', this.$t('MdTextSpeech.stopairecording'));
      this.$root.$refs.MdAIDictationSpeech.toggleSpeechBar();
      let controlId = null;

      try {
        // We try to get the controlId directly from the component that has the menu
        if (typeof this.getControlId === 'function') {
          controlId = this.getControlId();
        }
        // If it doesn't exist in this component, we look for it in the child (e.g. MdMemo)
        else if (this.$children && this.$children.length > 0) {
          const child = this.$children.find(
            (c) => typeof c.getControlId === 'function'
          );
          if (child) {
            controlId = child.getControlId();
          }
        }
        // If we still don't find it, we try to deduce it by the target
        if (!controlId && this.targetId) {
          controlId = this.targetId.replace(/^target-/, '');
        }

        if (controlId && this.$root.$refs.MdAIDictationSpeech) {
          this.$root.$refs.MdAIDictationSpeech.SpeechToTextFromMic(controlId);
        } else {
          console.warn('[ContextMenuMixin] No se pudo determinar el ControlId o SpeechBar no disponible.');
        }
      } catch (err) {
        console.error('[ContextMenuMixin] Error al iniciar Speech-to-Text:', err);
      }
    },

    handleSpeech() {
      EventBus.$emit('speechBarLabel', 'stop speech');
      this.$root.$refs.MdTextSpeech.toggleSpeechBar();
      this.$root.$refs.MdTextSpeech.startSpeech(this.inputValue);
    },

    // Methods to be implemented by the component
    updateValue() {
      throw new Error('updateValue must be implemented by component');
    },

    setChangedStatus() {
      throw new Error('setChangedStatus must be implemented by component');
    },

    getControlId() {
      throw new Error('getControlId must be implemented by component');
    },
    addSelectionListeners() {
      const inputElement = document.getElementById(this.inputElementId);
      if (inputElement) {
        // These events are fired when the selection changes
        inputElement.addEventListener('mouseup', this.handleSelectionChange);
        inputElement.addEventListener('keyup', this.handleSelectionChange);
        inputElement.addEventListener('select', this.handleSelectionChange);
      }
    },

    handleSelectionChange() {
      // Update the menu when the selection changes
      this.$nextTick(() => {
        this.loadMenuItems();
      });
    },

  },
  beforeDestroy() {
    // Limpiar listeners
    const inputElement = document.getElementById(this.inputElementId);
    if (inputElement) {
      inputElement.removeEventListener('mouseup', this.handleSelectionChange);
      inputElement.removeEventListener('keyup', this.handleSelectionChange);
      inputElement.removeEventListener('select', this.handleSelectionChange);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.addSelectionListeners();
    });
  },
};